import React, { useEffect, useRef, useState } from "react";
import { Smartphone, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const getScheme = () => {
  const raw = new URLSearchParams(window.location.search).get("scheme") || "koralove";
  return /^[a-z][a-z0-9+.-]*$/i.test(raw) ? raw : "koralove";
};

export default function MobileAuth() {
  const { user, loading } = useAuth();
  const [deepLink, setDeepLink] = useState(null);
  const [error, setError] = useState(null);
  const requested = useRef(false);
  const scheme = getScheme();

  useEffect(() => {
    if (loading || !user || requested.current) return;
    requested.current = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/mobile-code");
        const link = `${scheme}://auth?code=${encodeURIComponent(data.code)}`;
        setDeepLink(link);
        window.location.href = link;
      } catch (e) {
        setError("Impossible de générer le code de connexion. Réessayez.");
      }
    })();
  }, [loading, user, scheme]);

  const startGoogle = () => {
    sessionStorage.setItem("kora_mobile_auth", scheme);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "online",
      prompt: "select_account",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  return (
    <div className="min-h-screen bg-kora-sand flex items-center justify-center p-6" data-testid="mobile-auth-page">
      <div className="bg-white rounded-3xl border border-kora-line p-8 max-w-sm w-full text-center">
        <div className="w-16 h-16 rounded-full bg-kora-terracotta/10 flex items-center justify-center mx-auto mb-4">
          <Smartphone size={28} className="text-kora-terracotta" />
        </div>
        <h1 className="font-display font-black text-xl text-kora-ink">Connexion à l'application</h1>

        {loading ? (
          <Loader2 className="animate-spin text-kora-terracotta mx-auto mt-6" size={28} />
        ) : !user ? (
          <>
            <p className="text-sm text-kora-muted mt-2 mb-5">Connectez-vous avec Google pour ouvrir l'application mobile KoraLove.</p>
            <button
              data-testid="mobile-auth-google-btn"
              onClick={startGoogle}
              className="w-full bg-kora-terracotta text-white rounded-full py-3.5 font-bold"
            >
              Continuer avec Google
            </button>
          </>
        ) : error ? (
          <p data-testid="mobile-auth-error" className="text-sm text-red-500 mt-4">{error}</p>
        ) : (
          <>
            <p className="text-sm text-kora-muted mt-2 mb-5">
              Bonjour {(user.name || "").split(" ")[0]} 👋 — retour vers l'application…
            </p>
            {deepLink ? (
              <a
                data-testid="mobile-auth-open-app-btn"
                href={deepLink}
                className="block w-full bg-kora-terracotta text-white rounded-full py-3.5 font-bold"
              >
                Ouvrir l'application KoraLove
              </a>
            ) : (
              <Loader2 className="animate-spin text-kora-terracotta mx-auto" size={28} />
            )}
            <p className="text-[11px] text-kora-muted mt-4">
              Si rien ne se passe, touchez le bouton ci-dessus. Ce lien est valable 5 minutes.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
